import { useState } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { KeyRound, UserCircle2, Loader2, ShieldCheck } from "lucide-react";

export default function Settings() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_username: "", new_password: "", confirm_password: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.new_password && form.new_password !== form.confirm_password) {
      setError("Konfirmasi password baru tidak cocok");
      return;
    }
    if (!form.new_username && !form.new_password) {
      setError("Isi username baru atau password baru yang ingin diubah");
      return;
    }
    setSaving(true);
    try {
      const res = await api.put("/auth/credentials", {
        current_password: form.current_password,
        new_username: form.new_username || null,
        new_password: form.new_password || null,
      });
      setUser(res.data);
      toast.success("Akses login berhasil diperbarui");
      setForm({ current_password: "", new_username: "", new_password: "", confirm_password: "" });
    } catch (err) {
      setError(formatError(err, "Gagal memperbarui akses login"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="settings-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">Pengaturan</h1>
        <p className="text-sm text-slate-500 mt-1">Kelola akses login akun Anda</p>
      </div>

      <Card className="border-slate-200 shadow-sm" data-testid="account-info-card">
        <CardContent className="p-6 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0">
            <UserCircle2 className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-800" data-testid="settings-username">{user?.username}</p>
            <p className="text-xs text-slate-400">{user?.email}</p>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm" data-testid="credentials-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
              <KeyRound className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-800">Ubah Akses Login</h2>
              <p className="text-xs text-slate-400">Isi hanya bagian yang ingin diubah</p>
            </div>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="credentials-form">
            <div className="space-y-2">
              <Label htmlFor="current-password">Password Saat Ini <span className="text-rose-500">*</span></Label>
              <Input id="current-password" type="password" data-testid="current-password-input" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} placeholder="Wajib untuk konfirmasi" required autoComplete="current-password" className="focus-visible:ring-indigo-500" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-username">Username Baru</Label>
              <Input id="new-username" data-testid="new-username-input" value={form.new_username} onChange={(e) => setForm({ ...form, new_username: e.target.value })} placeholder={`Saat ini: ${user?.username} (kosongkan jika tidak diubah)`} autoComplete="username" className="focus-visible:ring-indigo-500" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">Password Baru</Label>
                <Input id="new-password" type="password" data-testid="new-password-input" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} placeholder="Minimal 6 karakter" autoComplete="new-password" className="focus-visible:ring-indigo-500" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Konfirmasi Password Baru</Label>
                <Input id="confirm-password" type="password" data-testid="confirm-password-input" value={form.confirm_password} onChange={(e) => setForm({ ...form, confirm_password: e.target.value })} placeholder="Ulangi password baru" autoComplete="new-password" className="focus-visible:ring-indigo-500" />
              </div>
            </div>
            {error && (
              <div data-testid="credentials-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-4 py-2.5">
                {error}
              </div>
            )}
            <div className="flex items-center justify-between pt-2">
              <p className="text-xs text-slate-400 flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
                Password tersimpan terenkripsi (bcrypt)
              </p>
              <Button type="submit" data-testid="credentials-submit" disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white transition-colors">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Perubahan"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
